/**
 * Branch selection and cache invalidation for `useOrganizationUsers`.
 *
 * Kept in its own leaf module, free of Apollo, React and Vite env imports, so
 * both rules can be unit-tested against fakes without standing up a renderer.
 */

export interface UseOrganizationUsersOptions {
  organizationId?: string;
  search?: string;
  pageSize?: number;
  offset?: number;
  enabled?: boolean;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
}

/**
 * True when the caller drives its own page, offset or search — a paginated
 * table, which needs the `users(...)` query.
 *
 * False for everyone else. Those callers render the whole roster and filter it
 * client-side, so they read the unbounded nested roster instead: `users(...)`
 * clamps to 500 server-side and silently drops members of larger orgs.
 */
export const shouldUsePaginatedQuery = (options: UseOrganizationUsersOptions): boolean =>
  options.pageSize !== undefined || options.offset !== undefined || !!options.search;


/** The slice of Apollo's cache these helpers touch. */
export interface RosterCache {
  identify(object: { __typename: string; organizationId: string }): string | undefined;
  evict(options: { id: string; fieldName: string }): boolean;
  gc(): string[];
}

/**
 * Drop the cached full roster for an org.
 *
 * Eviction rather than a refetch because `refetchQueries` only reaches queries
 * with a live observer — a picker that is unmounted when someone is deleted in
 * the team-roster table would otherwise serve stale cache when it next mounts.
 *
 * Returns whether anything was evicted.
 */
export const evictCachedRoster = (cache: RosterCache, orgId?: string): boolean => {
  if (!orgId) return false;

  const cacheId = cache.identify({ __typename: 'Organization', organizationId: orgId });
  if (!cacheId) return false;

  cache.evict({ id: cacheId, fieldName: 'users' });
  cache.gc();
  return true;
};

/**
 * Refresh both branches after a mutation.
 *
 * The branches are separate cache entries, so refreshing only the active one
 * leaves the other stale: a user invited or deleted from the paginated
 * team-roster table would linger in, or stay missing from, every picker.
 */
export const refreshAfterMutation = async <T>(
  cache: RosterCache,
  orgId: string | undefined,
  isPaginated: boolean,
  refetchPaged: () => Promise<T>,
  refetchRoster: () => Promise<T>
): Promise<T> => {
  evictCachedRoster(cache, orgId);
  return isPaginated ? refetchPaged() : refetchRoster();
};
